import { Suspense } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OutsourceAccessLogo } from "@/components/brand/OutsourceAccessLogo";
import { PageLoader } from "@/components/layout/PageLoader";
import { Sidebar } from "@/components/layout/Sidebar";

const ROLE_LABEL: Record<string, string> = {
  executive: "Executive",
  coo_office: "COO Office",
  function_owner: "Function Owner",
  read_only_viewer: "Read Only Viewer",
};

export function AppShell() {
  const { identity, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4 shadow-sm sm:px-6">
        <OutsourceAccessLogo />
        <div className="flex items-center gap-3">
          {identity && (
            <div className="hidden flex-col items-end leading-tight sm:flex">
              <span className="text-sm font-medium text-foreground">{identity.email}</span>
              <Badge variant="outline" className="mt-0.5">
                {ROLE_LABEL[identity.role] ?? identity.role}
              </Badge>
            </div>
          )}
          <Button variant="outline" size="icon" onClick={handleLogout} title="Sign out">
            <LogOut className="size-4" />
          </Button>
        </div>
      </header>
      <div className="flex flex-1">
        <Sidebar />
        <main className="w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 lg:py-8">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
